import { filterClasses } from "@swivl-mono/helpers";
import { Link, useLocation } from "react-router-dom";

import { classNameForSideBarItem, useVisibility } from "../../Hooks/WorksheetRow.Hooks";
import "./SidebarLink.scss";

interface Props {
  children: React.ReactNode;
  to: string | { pathname?: string; search?: string; hash?: string };
  height?: number;
  hideIf?: any;
  useATag?: boolean;
  supressMatch?: boolean;
  excludeMatch?: string;
  selected?: boolean;
  borderBottom?: boolean;
  groupId?: number;
  locationId?: number;
  greyBackground?: boolean;
  indent?: number;
  navIndex?: number;
  customizationCount?: number;
}

const doesPathMatch = (to, location) => {
  // Escape special characters in `to` to make it safe for regex
  if (typeof to === "string") {
    const escapedTo = to.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Create a regex that matches the exact `to` as a path segment
    const regex = new RegExp(`(^|/)${escapedTo}(/|$)`);
    // Test if the pathname matches the `to` exactly
    return regex.test(location.pathname);
  } else if (typeof to === "object" && "search" in to && location.search) {
    // Parse both search strings and check if all params from 'to' are present in location
    const toParams = new URLSearchParams(to.search);
    const locationParams = new URLSearchParams(location.search);

    // Check if all params from 'to' exist in location with the same values
    for (const [key, value] of toParams.entries()) {
      if (locationParams.get(key) !== value) {
        return false;
      }
    }

    // For hierarchical navigation (org -> group -> location), ensure more specific links don't match less specific ones
    // If the link has groupId but not locationId, and the URL has locationId, don't match (location is more specific)
    const linkHasGroupId = toParams.has("groupId");
    const linkHasLocationId = toParams.has("locationId");
    const urlHasLocationId = locationParams.has("locationId");

    if (linkHasGroupId && !linkHasLocationId && urlHasLocationId) {
      return false; // Don't highlight group link when on a location
    }

    return true;
  } else {
    return false;
  }
};

export function SidebarLink(props: Props) {
  const Tag: any = props.useATag ? "a" : Link;
  const visibility = useVisibility({ hideIf: props.hideIf, groupId: props.groupId, locationId: props.locationId });
  let selected = props.selected;

  if (!props.supressMatch) {
    let location = useLocation();

    if (doesPathMatch(props.to, location)) {
      if (props.excludeMatch && location.pathname.indexOf(props.excludeMatch) > -1) {
        selected = false;
      } else {
        selected = true;
      }
    }
  }

  return (
    <Tag
      {...(props.useATag ? { href: props.to } : { to: props.to })}
      {...(props.navIndex !== undefined ? { "data-nav-index": props.navIndex } : {})}
      style={{
        paddingLeft: (props.indent || 1) + "em",
        height: (props.height || 44) + "px"
      }}
      className={filterClasses(
        classNameForSideBarItem("link", visibility),
        `SidebarLink ${selected ? "selected" : ""} ${props.borderBottom ? "borderBottom" : ""} ${props.greyBackground ? "greyBackground" : ""}`
      )}
    >
      <div className="chit" />
      <div className="flex-1">{props.children}</div>
      {props.customizationCount != null && props.customizationCount > 0 && (
        <span
          className={filterClasses(
            "flex items-center justify-center rounded-full whitespace-nowrap",
            "bg-grey-50 text-black",
            "text-[12px] leading-none font-semibold",
            "mr-2 h-[15px] px-1.5"
          )}
        >
          {props.customizationCount}
        </span>
      )}
    </Tag>
  );
}
